import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { useLanguage } from '../context/LanguageContext';
import { useTheme } from '../context/ThemeContext';
import { evenementsData } from '../data/newModules';
import AnimatedSection from '../components/common/AnimatedSection';
import SocialShare from '../components/common/SocialShare';
import { 
  Calendar, MapPin, Clock, Users, Video, ArrowRight,
  CalendarPlus, CheckCircle, ExternalLink, Share2
} from 'lucide-react';

const EvenementsPage = () => {
  const { language } = useLanguage();
  const { isDark } = useTheme();
  const content = evenementsData[language] || evenementsData.fr;
  const [showPast, setShowPast] = useState(false);

  const typeColors = {
    cercle: { bg: isDark ? 'bg-[#5B2D8E]/20' : 'bg-[#5B2D8E]/10', text: isDark ? 'text-[#9B7FCE]' : 'text-[#5B2D8E]', border: 'border-[#5B2D8E]' },
    webinaire: { bg: isDark ? 'bg-blue-500/20' : 'bg-blue-100', text: 'text-blue-500', border: 'border-blue-500' },
    terrain: { bg: isDark ? 'bg-green-500/20' : 'bg-green-100', text: 'text-green-500', border: 'border-green-500' },
    formation: { bg: isDark ? 'bg-[#FFC107]/20' : 'bg-[#FFC107]/10', text: isDark ? 'text-[#FFC107]' : 'text-[#B8860B]', border: 'border-[#FFC107]' }
  };

  return (
    <div className="pt-20">
      {/* Hero */}
      <section className={`py-16 ${isDark ? 'bg-[#1C1917]' : 'bg-gradient-to-br from-[#F5F0E8] to-[#FAFAF9]'}`}>
        <div className="container-custom">
          <div className="max-w-3xl mx-auto text-center">
            <AnimatedSection>
              <Calendar className={`w-16 h-16 mx-auto mb-6 ${isDark ? 'text-[#FFC107]' : 'text-[#5B2D8E]'}`} />
              <h1 className="heading-1 mb-4">{content.title}</h1>
              <div className="divider mx-auto mb-6" />
              <p className="body-large">{content.subtitle}</p>
            </AnimatedSection>
          </div>
        </div>
      </section>

      {/* Event Types Legend */}
      <section className={`py-6 border-b ${isDark ? 'bg-[#0C0A09] border-white/10' : 'bg-white border-stone-200'}`}>
        <div className="container-custom">
          <div className="flex flex-wrap justify-center gap-4">
            {Object.entries(content.eventTypes).map(([key, type]) => {
              const colors = typeColors[key];
              return (
                <div key={key} className="flex items-center gap-2">
                  <span className={`w-3 h-3 rounded-full ${colors.bg} ${colors.border} border-2`} />
                  <span className={`text-sm ${colors.text}`}>{type.label}</span>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Upcoming Events */}
      <section className={`section ${isDark ? 'bg-[#0C0A09]' : 'bg-[#FAFAF9]'}`}>
        <div className="container-custom">
          <div className="max-w-4xl mx-auto">
            <AnimatedSection>
              <h2 className="heading-2 mb-8">À venir</h2>
            </AnimatedSection>

            <div className="grid md:grid-cols-2 gap-6">
              {content.upcoming.map((event, index) => {
                const typeConfig = content.eventTypes[event.type];
                const colors = typeColors[event.type] || typeColors.cercle;
                const isFull = event.registered >= event.capacity;
                const fillPercent = (event.registered / event.capacity) * 100;

                return (
                  <AnimatedSection key={event.id} delay={index * 0.1}>
                    <div className={`rounded-2xl overflow-hidden h-full ${isDark ? 'bg-[#1C1917]' : 'bg-white'} border ${isDark ? 'border-white/10' : 'border-stone-200'}`}>
                      {/* Event Type Banner */}
                      <div className={`px-4 py-2 ${colors.bg} border-l-4 ${colors.border} flex items-center justify-between`}>
                        <span className={`text-sm font-medium ${colors.text}`}>{typeConfig?.label}</span>
                        {event.isOnline && (
                          <span className="flex items-center gap-1 text-xs text-blue-500">
                            <Video className="w-3 h-3" /> En ligne
                          </span>
                        )}
                      </div>

                      {/* Content */}
                      <div className="p-5">
                        <h3 className="text-lg font-bold text-theme-primary mb-3">{event.title}</h3>
                        
                        {/* Date & Time */}
                        <div className="flex flex-wrap gap-4 mb-3 text-sm text-theme-secondary">
                          <span className="flex items-center gap-1">
                            <Calendar className="w-4 h-4" />
                            {new Date(event.date).toLocaleDateString('fr-FR', { weekday: 'long', day: 'numeric', month: 'long' })}
                          </span>
                          <span className="flex items-center gap-1">
                            <Clock className="w-4 h-4" />
                            {event.time}
                          </span>
                        </div>

                        {/* Location */}
                        <div className="flex items-start gap-1 mb-4 text-sm text-theme-secondary">
                          <MapPin className="w-4 h-4 mt-0.5 flex-shrink-0" />
                          <div>
                            <span className="font-medium text-theme-primary">{event.location}</span>
                            {event.address && <span className="block text-xs">{event.address}</span>}
                          </div>
                        </div>

                        {/* Description */}
                        <p className="text-sm text-theme-secondary mb-4">{event.description}</p>

                        {/* Capacity Bar */}
                        <div className="mb-4">
                          <div className="flex items-center justify-between text-xs mb-1">
                            <span className={`flex items-center gap-1 ${isDark ? 'text-stone-400' : 'text-stone-500'}`}>
                              <Users className="w-3 h-3" />
                              {event.registered} / {event.capacity} inscrits
                            </span>
                            <span className={isFull ? 'text-red-500' : 'text-green-500'}>
                              {isFull ? 'Complet' : `${event.capacity - event.registered} places restantes`}
                            </span>
                          </div>
                          <div className={`h-2 rounded-full ${isDark ? 'bg-white/10' : 'bg-stone-200'}`}>
                            <div 
                              className={`h-full rounded-full transition-all ${isFull ? 'bg-red-500' : 'bg-green-500'}`}
                              style={{ width: `${Math.min(fillPercent, 100)}%` }}
                            />
                          </div>
                        </div>

                        {/* Actions */}
                        <div className="flex gap-2">
                          <button 
                            className={`flex-1 flex items-center justify-center gap-2 px-4 py-2.5 rounded-full text-sm font-medium transition-colors ${
                              isFull 
                                ? isDark ? 'bg-white/10 text-stone-500 cursor-not-allowed' : 'bg-stone-100 text-stone-400 cursor-not-allowed'
                                : 'bg-[#5B2D8E] text-white hover:bg-[#7C4DAF]'
                            }`}
                            disabled={isFull}
                            data-testid={`register-${event.id}`}
                          >
                            {isFull ? content.cta.full : content.cta.register}
                          </button>
                          <button 
                            className={`p-2.5 rounded-full ${isDark ? 'bg-white/10 text-stone-300 hover:bg-white/20' : 'bg-stone-100 text-stone-600 hover:bg-stone-200'}`}
                            title={content.cta.addToCalendar}
                            data-testid={`calendar-${event.id}`}
                          >
                            <CalendarPlus className="w-5 h-5" />
                          </button>
                          <SocialShare
                            title={event.title}
                            text={`Événement ADA : ${event.title} — ${new Date(event.date).toLocaleDateString('fr-FR', { weekday: 'long', day: 'numeric', month: 'long' })} à ${event.location}`}
                            url={`${window.location.origin}/evenements#${event.id}`}
                            hashtags={['ADACI', 'Evenement', event.location.replace(/\s/g, '')]}
                            compact={true}
                          />
                        </div>
                      </div>
                    </div>
                  </AnimatedSection>
                );
              })}
            </div>
          </div>
        </div>
      </section>

      {/* Past Events */}
      <section className={`section ${isDark ? 'bg-[#1C1917]' : 'bg-[#F5F0E8]'}`}>
        <div className="container-custom">
          <div className="max-w-4xl mx-auto">
            <AnimatedSection>
              <button
                onClick={() => setShowPast(!showPast)}
                className="flex items-center gap-2 heading-3 mb-6"
              >
                Événements passés
                <span className={`px-2 py-0.5 rounded-full text-sm ${isDark ? 'bg-white/10 text-stone-400' : 'bg-stone-200 text-stone-500'}`}>
                  {content.past.length}
                </span>
              </button>
            </AnimatedSection>

            {showPast && (
              <div className="space-y-4">
                {content.past.map((event, index) => (
                  <AnimatedSection key={event.id} delay={index * 0.1}>
                    <div className={`rounded-xl p-4 flex items-center justify-between ${isDark ? 'bg-[#0C0A09]' : 'bg-white'} border ${isDark ? 'border-white/10' : 'border-stone-200'}`}>
                      <div className="flex items-center gap-4">
                        <div className={`w-12 h-12 rounded-lg flex items-center justify-center ${isDark ? 'bg-white/10' : 'bg-stone-100'}`}>
                          <CheckCircle className="w-6 h-6 text-green-500" />
                        </div>
                        <div>
                          <h4 className="font-semibold text-theme-primary">{event.title}</h4>
                          <div className="flex items-center gap-3 text-sm text-theme-secondary">
                            <span>{new Date(event.date).toLocaleDateString('fr-FR', { day: 'numeric', month: 'long', year: 'numeric' })}</span>
                            <span>•</span>
                            <span>{event.location}</span>
                          </div>
                        </div>
                      </div>
                      <Link to="/terrain" className={`flex items-center gap-1 text-sm font-medium ${isDark ? 'text-[#FFC107]' : 'text-[#5B2D8E]'}`}>
                        Voir récap <ExternalLink className="w-4 h-4" />
                      </Link>
                    </div>
                  </AnimatedSection>
                ))}
              </div>
            )}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 bg-gradient-to-br from-[#5B2D8E] to-[#7C4DAF]">
        <div className="container-custom">
          <div className="max-w-2xl mx-auto text-center">
            <AnimatedSection>
              <h2 className="heading-2 text-white mb-4">{content.cta.proposeEvent}</h2>
              <p className="text-xl text-white/80 mb-8">
                Vous souhaitez organiser un événement ADA dans votre ville ou votre organisation ?
              </p>
              <Link to="/rejoindre" className="btn-secondary">
                Nous contacter
                <ArrowRight className="w-5 h-5" />
              </Link>
            </AnimatedSection>
          </div>
        </div>
      </section>
    </div>
  );
};

export default EvenementsPage;
